import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import PageHeader from '../../components/PageHeader.jsx';
import StatusBadge from '../../components/StatusBadge.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import UserSearchSelect from '../../components/UserSearchSelect.jsx';
import { ComplaintsAPI } from '../../lib/api';
import { complaintSerial } from '../../lib/complaintSerial';
import { useAuth } from '../../lib/auth.jsx';
import { buildPath, COMPLAINT_STATUS, complaintCapabilities } from '../../config';

export default function ComplaintDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Auretics portal users get a read-only view of their OWN ticket.
  // Every write control below is gated on this — see
  // ``complaintCapabilities`` in config.js and the matching
  // server-side refusal in ``ComplaintViewSet._deny_external_write``.
  const can = complaintCapabilities(user);


  const [c, setC] = useState(null);
  const [reassignUser, setReassignUser] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const knownAssignees = useMemo(() => {
    if (!c?.assign_to) return [];
    return [{ id: c.assign_to, email: c.assign_to_email || '' }];
  }, [c]);

  useEffect(() => {
    ComplaintsAPI.retrieve(id)
      .then((complaint) => {
        setC(complaint);
        setReassignUser(complaint.assign_to ? String(complaint.assign_to) : '');
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [id]);


  const onStatusChange = async (status) => {
    setBusy(true);
    try {
      const updated = await ComplaintsAPI.setStatus(id, status);
      setC(updated);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const onReassign = async () => {
    if (!reassignUser) return;
    setBusy(true);
    try {
      const updated = await ComplaintsAPI.reassign(id, reassignUser);
      setC(updated);
      setReassignUser(updated.assign_to ? String(updated.assign_to) : '');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Delete this complaint? This cannot be undone.')) return;
    setBusy(true);
    try {
      await ComplaintsAPI.remove(id);
      navigate(buildPath.complaintsList('my_complaints'));
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  if (loading) return <Loader />;
  if (!c) return <ErrorBox error={error || new Error('Complaint not found.')} />;

  return (
    <>
      <PageHeader
        title={`Ticket #${complaintSerial(c)}`}
        subtitle={c.complaint_title}
        actions={
          // External users have no lifecycle controls at all — the
          // support team owns the ticket once it's filed.
          (can.canEdit || can.canDelete) ? (
            <>
              {can.canEdit && (
                <Link className="ty-btn ty-btn--ghost" to={buildPath.complaintEdit(c.id)}>Edit</Link>
              )}
              {can.canDelete && (
                <button className="ty-btn ty-btn--danger" onClick={onDelete} disabled={busy}>Delete</button>
              )}
            </>
          ) : null
        }
      />


      <ErrorBox error={error} />

      <div className="ty-card">
        <div className="ty-row" style={{ justifyContent: 'space-between' }}>
          <StatusBadge domain="complaint" value={c.status} />
          {/* Read-only for external users: they see WHERE their ticket
              is, but only the support team can move it. */}
          {can.canChangeStatus && (
            <select
              className="ty-select"
              value={c.status}
              onChange={(e) => onStatusChange(e.target.value)}
              disabled={busy}
              style={{ width: 'auto' }}
            >
              {COMPLAINT_STATUS.map((s) => (
                <option key={s.value} value={s.value}>Set to: {s.label}</option>
              ))}
            </select>
          )}
        </div>


        <hr />
        <p style={{ whiteSpace: 'pre-wrap' }}>
          {c.description || <em className="ty-muted">No description.</em>}
        </p>

        <div className="ty-grid-2 ty-mt-2">
          <div><strong>Division:</strong> {c.division_name || '—'}</div>
          <div><strong>Sub-division:</strong> {c.sub_divisions || '—'}</div>
          <div><strong>Assigned to:</strong> {c.assign_to_email || '—'}</div>
          <div><strong>Created by:</strong> {c.created_by_email || '—'}</div>
          <div><strong>Date:</strong> {c.date || '—'}</div>
          <div><strong>Mobile:</strong> {c.mobile_number || '—'}</div>
          <div><strong>Email:</strong> {c.email || '—'}</div>
          <div><strong>Registration #:</strong> {c.registeration_number || '—'}</div>
        </div>

        {c.file_name && (
          <p className="ty-mt-2">
            <strong>Attachment:</strong> {c.file_name}
          </p>
        )}
      </div>

      {/* Routing the ticket is a support-desk action, and the picker
          would leak the customer's staff directory to an outside
          complainant.  Hidden AND refused server-side. */}
      {can.canReassign && (
        <div className="ty-card ty-mt-3">
          <h3>Reassign</h3>
          <div className="ty-field">
            <label htmlFor="complaint-reassign">Assign to</label>
            <UserSearchSelect
              id="complaint-reassign"
              multiple={false}
              value={reassignUser ? [Number(reassignUser)] : []}
              onChange={(ids) => setReassignUser(ids[0] ?? '')}
              knownUsers={knownAssignees}
              selfId={user?.id}
              disabled={busy}
            />
            <small className="ty-muted">
              Search by name, mobile or email. Three recent assignees appear as
              suggestions when you open the field.
            </small>
          </div>
          <div className="ty-row ty-row--end ty-mt-2">
            <button
              className="ty-btn"
              onClick={onReassign}
              disabled={
                busy ||
                !reassignUser ||
                Number(reassignUser) === Number(c.assign_to)
              }
            >
              Reassign
            </button>
          </div>
        </div>
      )}

      {/* Investigation / corrective / preventive notes are the support
          team's internal working record.  An external complainant sees
          the ticket's STATUS, not the internal analysis behind it — so
          for them we show a short progress note instead. */}
      {can.isExternal && (
        <div className="ty-card ty-mt-3">
          <h3>Progress</h3>
          <p className="ty-muted">
            Our support team will update this ticket as they work on it.
            You&rsquo;ll be notified by email whenever the status changes.
          </p>
        </div>
      )}

      {!can.isExternal && (
        <div className="ty-card ty-mt-3">
          <h3>Resolution notes</h3>
          <div className="ty-grid-2">
            <div><strong>Result of investigation:</strong><div>{c.result_of_investigation || <em className="ty-muted">—</em>}</div></div>
            <div><strong>Corrective action:</strong><div>{c.corrective_action_taken || <em className="ty-muted">—</em>}</div></div>
            <div><strong>Preventive action:</strong><div>{c.prevention_action_taken || <em className="ty-muted">—</em>}</div></div>
          </div>
        </div>
      )}
    </>
  );
}


